/**
 * WarehouseMap - Main page for interactive warehouse map editor
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Divider,
  CircularProgress,
  TextField,
  Autocomplete,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Drawer,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Save as SaveIcon,
  ImageSearch as ScanIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  FileUpload as ImportIcon,
  Home as HomeIcon,
  Search as SearchIcon,
  Route as RouteIcon,
  Inventory as InventoryIcon,
  Close as CloseIcon,
  ViewInAr as View3DIcon,
  Map as View2DIcon,
  DesktopWindows as DesktopIcon,
  AddLocation as AddLocationIcon,
  Menu as MenuIcon,
  Warning as WarningIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import MapCanvas from '../../components/WarehouseMap/MapCanvas';
import ElementPalette from '../../components/WarehouseMap/ElementPalette';
import PropertiesPanel from '../../components/WarehouseMap/PropertiesPanel';
import WarehouseLocationsPanel from '../../components/WarehouseMap/WarehouseLocationsPanel';
import RackDetailView from '../../components/WarehouseMap/RackDetailView';
import ScanUploader from '../../components/WarehouseMap/ScanUploader';
import MapImporter from '../../components/WarehouseMap/MapImporter';
import Warehouse3DView from '../../components/WarehouseMap/Warehouse3DView';
import { useWarehouse } from '../../contexts/WarehouseContext';
import axios from 'axios';
import { API_BASE_URL } from '../../utils/apiConfig';
import {
  MapElement,
  ElementType,
  EditorMode,
  ELEMENT_TYPES,
  ScanResult,
  DetectedElement,
  Position,
} from '../../components/WarehouseMap/types';
import { PickingPath, PickingStrategy } from '../../components/WarehouseMap/pathTypes';
import {
  calculatePickingPath,
  getNavigationGraph,
  getUnplacedLocations,
  autoPlaceLocations,
  getProductLocations,
  printLocationLabels,
  openPdfBlobInBrowser,
  BULK_LABEL_INCLUDE_TYPES,
} from '../../services/locationApi';
import type { UnplacedLocation } from '../../services/locationApi';
import { productsApi, ProductApiResponse } from '../../services/productsApi';
import type { NavigationGraph } from '../../components/WarehouseMap/pathTypes';

const WarehouseMap: React.FC = () => {
  // Get warehouses from global context
  const { warehouses, selectedWarehouse, setSelectedWarehouse, refreshWarehouses } = useWarehouse();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [elements, setElements] = useState<MapElement[]>([]);
  const [initialElementIds, setInitialElementIds] = useState<Set<string>>(new Set());
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [mode, setMode] = useState<EditorMode>('select');
  const [showGrid, setShowGrid] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showScanDialog, setShowScanDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [printingBulkLabels, setPrintingBulkLabels] = useState(false);
  const [printingLabelRowId, setPrintingLabelRowId] = useState<number | null>(null);

  /** Map = canvas editor; Locations = list + manage shortcuts */
  const [mainTab, setMainTab] = useState<'map' | 'locations'>('map');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  // View mode: 2D editor or 3D visualization
  const [viewMode, setViewMode] = useState<'2d' | '3d'>('2d');

  // Rack detail dialog (double-click on rack)
  const [rackDetailOpen, setRackDetailOpen] = useState(false);
  const [rackDetailLocationId, setRackDetailLocationId] = useState<number | null>(null);

  // Unplaced locations
  const [unplacedLocations, setUnplacedLocations] = useState<UnplacedLocation[]>([]);
  const [unplacedCount, setUnplacedCount] = useState(0);
  const [showUnplacedPanel, setShowUnplacedPanel] = useState(false);

  // Mobile bottom drawer
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Product finder & picking path
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSearchResults, setProductSearchResults] = useState<ProductApiResponse[]>([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Array<{ product: ProductApiResponse; quantity: number }>>([]);
  const [pickingPath, setPickingPath] = useState<PickingPath | null>(null);
  const [pickingPathLoading, setPickingPathLoading] = useState(false);
  const [showPickingPath, setShowPickingPath] = useState(false);
  const [pickingStrategy, setPickingStrategy] = useState<PickingStrategy>('shortest');
  const [showFinder, setShowFinder] = useState(false);
  const [highlightedLocationIds, setHighlightedLocationIds] = useState<number[]>([]);
  const [navigationGraph, setNavigationGraph] = useState<NavigationGraph | null>(null);
  const [showNavigation, setShowNavigation] = useState(false);

  // Undo/redo history
  const [history, setHistory] = useState<MapElement[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Refs for debounced history
  const historyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingElementsRef = useRef<MapElement[] | null>(null);

  // API_BASE_URL is imported at the top

  // Show snackbar helper (defined early so it can be used in other callbacks)
  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Search products with debounce
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleProductSearch = useCallback((query: string) => {
    setProductSearchQuery(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!query || query.length < 2) {
      setProductSearchResults([]);
      return;
    }
    searchTimeoutRef.current = setTimeout(async () => {
      setProductSearchLoading(true);
      try {
        const result = await productsApi.getProducts({
          search: query,
          page_size: 15,
        });
        setProductSearchResults(result.results || []);
      } catch (err) {
        console.error('Product search failed:', err);
      } finally {
        setProductSearchLoading(false);
      }
    }, 300);
  }, []);

  // Add product to picking list and highlight its locations
  const addProductToPick = useCallback((product: ProductApiResponse) => {
    setSelectedProducts(prev => {
      const existing = prev.find(p => p.product.id === product.id);
      if (existing) return prev;
      return [...prev, { product, quantity: 1 }];
    });
    setProductSearchQuery('');
    setProductSearchResults([]);

    if (selectedWarehouse) {
      getProductLocations(product.id, selectedWarehouse.id).then((detail) => {
        const rackIds = detail.locations
          .map(loc => loc.rack?.id)
          .filter((id): id is number => id != null);
        if (rackIds.length > 0) {
          setHighlightedLocationIds(prev => Array.from(new Set([...prev, ...rackIds])));
          showSnackbar(
            `${product.name}: ${detail.locationsCount} lokalizacji, ${detail.totalAvailable} szt. dostępnych`,
            'info'
          );
        }
      }).catch(() => { /* silent */ });
    }
  }, [selectedWarehouse, showSnackbar]);

  // Update product quantity
  const updateProductQuantity = useCallback((productId: number, quantity: number) => {
    setSelectedProducts(prev =>
      prev.map(p => p.product.id === productId ? { ...p, quantity: Math.max(1, quantity) } : p)
    );
  }, []);

  // Remove product from list and clear highlights if no products left
  const removeProductFromPick = useCallback((productId: number) => {
    setSelectedProducts(prev => {
      const next = prev.filter(p => p.product.id !== productId);
      if (next.length === 0) {
        setHighlightedLocationIds([]);
        setPickingPath(null);
        setShowPickingPath(false);
      }
      return next;
    });
  }, []);

  // Calculate picking path
  const handleCalculatePath = useCallback(async () => {
    if (!selectedWarehouse || selectedProducts.length === 0) return;
    setPickingPathLoading(true);
    try {
      const result = await calculatePickingPath(selectedWarehouse.id, {
        items: selectedProducts.map(p => ({
          productId: p.product.id,
          quantity: p.quantity,
        })),
        strategy: pickingStrategy,
      });
      setPickingPath(result);
      setShowPickingPath(true);

      // Highlight all racks involved in the picking path
      const rackIds = result.steps
        .map(s => s.rack?.id)
        .filter((id): id is number => id != null);
      const uniqueRackIds = Array.from(new Set(rackIds));
      setHighlightedLocationIds(uniqueRackIds);

      if (result.steps.length === 0) {
        showSnackbar('Nie znaleziono produktów w magazynie', 'error');
      } else {
        const msg = result.isComplete
          ? `Ścieżka wyznaczona: ${result.steps.length} kroków`
          : `Ścieżka wyznaczona: ${result.steps.length} kroków (brak pełnej ilości!)`;
        showSnackbar(msg, result.isComplete ? 'success' : 'warning');
      }
    } catch (err: any) {
      console.error('Failed to calculate picking path:', err);
      const msg = err?.response?.data?.error || err?.response?.data?.detail || 'Nie udało się wyznaczyć ścieżki';
      showSnackbar(msg, 'error');
    } finally {
      setPickingPathLoading(false);
    }
  }, [selectedWarehouse, selectedProducts, pickingStrategy, showSnackbar]);

  /**
   * Generate location PDF labels. Opens a blank tab synchronously before the async
   * request so popup blockers do not block the PDF tab.
   */
  const openLocationLabelsPdf = useCallback(
    (
      options: { locationIds?: number[]; includeTypes?: string[] },
      rowTrackingId?: number
    ) => {
      if (!selectedWarehouse) return;
      const preview = window.open('', '_blank');
      if (rowTrackingId != null) setPrintingLabelRowId(rowTrackingId);
      else setPrintingBulkLabels(true);
      printLocationLabels(selectedWarehouse.id, options)
        .then((blobUrl) => {
          openPdfBlobInBrowser(preview, blobUrl);
          showSnackbar(t('warehouseMap.labelsPdfOpened'), 'success');
        })
        .catch((err: unknown) => {
          if (preview && !preview.closed) preview.close();
          console.error('Failed to generate labels:', err);
          const msg = err instanceof Error ? err.message : t('warehouseMap.labelsPdfError');
          showSnackbar(msg, 'error');
        })
        .finally(() => {
          setPrintingBulkLabels(false);
          setPrintingLabelRowId(null);
        });
    },
    [selectedWarehouse, showSnackbar, t]
  );

  const handlePrintAllShelfLabels = useCallback(() => {
    openLocationLabelsPdf({ includeTypes: [...BULK_LABEL_INCLUDE_TYPES] });
  }, [openLocationLabelsPdf]);

  // Load navigation graph
  const loadNavigationGraph = useCallback(async () => {
    if (!selectedWarehouse) return;
    try {
      const graph = await getNavigationGraph(selectedWarehouse.id);
      setNavigationGraph(graph);
    } catch (err) {
      console.error('Failed to load navigation graph:', err);
    }
  }, [selectedWarehouse]);

  // Rack detail double-click handler
  const handleRackDoubleClick = useCallback((element: MapElement) => {
    const numericId = Number(element.id);
    if (!isNaN(numericId) && numericId > 0) {
      setRackDetailLocationId(numericId);
      setRackDetailOpen(true);
    }
  }, []);

  // Load unplaced locations
  const loadUnplacedLocations = useCallback(async () => {
    if (!selectedWarehouse) return;
    try {
      const data = await getUnplacedLocations(selectedWarehouse.id);
      setUnplacedLocations(data.locations);
      setUnplacedCount(data.count);
    } catch (err) {
      console.error('Failed to load unplaced locations:', err);
    }
  }, [selectedWarehouse]);

  // Load nav graph and unplaced locations when warehouse changes
  useEffect(() => {
    if (selectedWarehouse) {
      loadNavigationGraph();
      loadUnplacedLocations();
    }
  }, [selectedWarehouse, loadNavigationGraph, loadUnplacedLocations]);

  // Handle URL query params (from DocumentDetails/ProductDetails "Pokaż na mapie" button)
  const urlParamsProcessed = useRef(false);
  useEffect(() => {
    if (urlParamsProcessed.current) return;
    const docId = searchParams.get('document');
    const whId = searchParams.get('warehouse');
    const strategyParam = searchParams.get('strategy') as PickingStrategy | null;
    const viewParam = searchParams.get('view');
    const highlightParam = searchParams.get('highlight');
    const productIdParam = searchParams.get('productId');
    const productNameParam = searchParams.get('productName');
    const productCodeParam = searchParams.get('productCode');

    if (!whId) return;

    // Select the warehouse if not already selected
    const targetWarehouse = warehouses.find(w => w.id === Number(whId));
    if (targetWarehouse && (!selectedWarehouse || selectedWarehouse.id !== targetWarehouse.id)) {
      setSelectedWarehouse(targetWarehouse);
      return; // Wait for warehouse to be set, this effect will re-run
    }

    if (!selectedWarehouse || selectedWarehouse.id !== Number(whId)) return;

    urlParamsProcessed.current = true;

    // Set view mode
    if (viewParam === '3d') setViewMode('3d');

    // Set strategy
    if (strategyParam) setPickingStrategy(strategyParam);

    // Handle highlight locations from product view
    if (highlightParam) {
      const locationIds = highlightParam.split(',').map(Number).filter(id => !isNaN(id));
      if (locationIds.length > 0) {
        setHighlightedLocationIds(locationIds);
      }

      // Auto-add the product to the finder and calculate picking path
      if (productIdParam && productNameParam) {
        setShowFinder(true);
        const productId = Number(productIdParam);
        const productForList: ProductApiResponse = {
          id: productId,
          name: productNameParam,
          product_code: productCodeParam || '',
        } as ProductApiResponse;
        setSelectedProducts(prev => {
          if (prev.find(p => p.product.id === productForList.id)) return prev;
          return [...prev, { product: productForList, quantity: 1 }];
        });

        // Auto-calculate picking path for this product
        setPickingPathLoading(true);
        calculatePickingPath(Number(whId), {
          items: [{ productId, quantity: 1 }],
          strategy: pickingStrategy,
        }).then((result) => {
          setPickingPath(result);
          setShowPickingPath(true);
          const rackIds = result.steps.map(s => s.rack?.id).filter((id): id is number => id != null);
          setHighlightedLocationIds(Array.from(new Set(rackIds)));
          if (result.steps.length === 0) {
            showSnackbar('Nie znaleziono produktu w magazynie', 'error');
          } else {
            showSnackbar(`Ścieżka wyznaczona: ${result.steps.length} kroków`, 'success');
          }
        }).catch((err: any) => {
          console.error('Failed to auto-calculate picking path:', err);
          const msg = err?.response?.data?.error || err?.response?.data?.detail || 'Nie udało się wyznaczyć ścieżki';
          showSnackbar(msg, 'error');
        }).finally(() => {
          setPickingPathLoading(false);
        });
      }

      setSearchParams({}, { replace: true });
    }

    // Auto-calculate picking path for document
    if (docId) {
      setShowFinder(true);
      setPickingPathLoading(true);

      calculatePickingPath(Number(whId), {
        documentId: Number(docId),
        strategy: strategyParam || 'shortest',
      }).then((result) => {
        setPickingPath(result);
        setShowPickingPath(true);
        const rackIds = result.steps.map(s => s.rack?.id).filter((id): id is number => id != null);
        setHighlightedLocationIds(Array.from(new Set(rackIds)));
        setSearchParams({}, { replace: true });
      }).catch((err) => {
        console.error('Failed to auto-calculate picking path:', err);
        showSnackbar('Nie udało się obliczyć ścieżki z dokumentu', 'error');
      }).finally(() => {
        setPickingPathLoading(false);
      });
    }
  }, [searchParams, warehouses, selectedWarehouse, setSelectedWarehouse, showSnackbar, setSearchParams, pickingStrategy]);

  // Load map data for warehouse
  const loadMapData = useCallback(async (warehouseId: number) => {
    setLoading(true);
    // Clear elements immediately to prevent rendering stale data
    setElements([]);
    setHistory([]);
    setHistoryIndex(0);

    try {
      // Use axios instead of fetch to benefit from token refresh interceptor
      const response = await axios.get(
        `${API_BASE_URL}/warehouses/${warehouseId}/map_data/`
      );

      const data = response.data;

      // Validate response structure
      if (!data || !Array.isArray(data.locations)) {
        showSnackbar(t('warehouseMap.messages.invalidDataFormat'), 'error');
        setElements([]);
        setHistory([]);
        setHistoryIndex(0);
        return;
      }

      // Convert API data to MapElement format, filtering out invalid dimensions
      const mapElements: MapElement[] = data.locations
        .filter((loc: any) =>
          loc &&
          loc.dimensions &&
          typeof loc.dimensions.width === 'number' &&
          typeof loc.dimensions.height === 'number' &&
          loc.dimensions.width > 0 &&
          loc.dimensions.height > 0 &&
          isFinite(loc.dimensions.width) &&
          isFinite(loc.dimensions.height) &&
          isFinite(loc.position?.x || 0) &&
          isFinite(loc.position?.y || 0)
        )
        .map((loc: any) => ({
          id: String(loc.id),
          code: loc.code || '',
          name: loc.name || '',
          type: loc.type || 'SLOT',
          position: {
            x: isFinite(loc.position?.x) ? Number(loc.position.x) : 0,
            y: isFinite(loc.position?.y) ? Number(loc.position.y) : 0,
          },
          dimensions: {
            width: Math.max(20, Number(loc.dimensions.width)),  // Ensure minimum size
            height: Math.max(20, Number(loc.dimensions.height)),
          },
          rotation: isFinite(loc.rotation) ? Number(loc.rotation) : 0,
          color: loc.color || '#e3f2fd',
          status: loc.status || 'EMPTY',
          stockCount: loc.stock_count,
          maxItems: loc.max_items,
          parentId: loc.parent_id,
          shelves: loc.shelves?.map((s: any) => ({
            level: s.level,
            heightCm: s.height_cm,
            slotCount: s.slot_count,
            totalItems: s.total_items,
            topProducts: s.top_products || [],
          })),
        }));

      setElements(mapElements);
      setHistory([mapElements]);
      setHistoryIndex(0);

      // Track which elements were originally in the database (not new/scan)
      const dbElementIds = new Set(
        mapElements
          .filter(el => el.id && !el.id.startsWith('new-') && !el.id.startsWith('scan-'))
          .map(el => el.id)
      );
      setInitialElementIds(dbElementIds);
    } catch (error: any) {
      console.error('Failed to load map data:', error);

      // Handle axios errors
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.detail ||
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          t('warehouseMap.messages.unknownError');

        // Check if it's a token expiration error (should be handled by interceptor, but just in case)
        if (error.response?.status === 401) {
          showSnackbar(t('warehouseMap.messages.sessionExpired'), 'error');
        } else if (error.response?.status) {
          showSnackbar(t('warehouseMap.messages.serverError', { status: error.response.status, message: errorMessage }), 'error');
        } else if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
          showSnackbar(t('warehouseMap.messages.networkError'), 'error');
        } else {
          showSnackbar(t('warehouseMap.messages.loadError', { message: errorMessage }), 'error');
        }
      } else {
        const errorMessage = error instanceof Error ? error.message : t('warehouseMap.messages.unknownError');
        showSnackbar(t('warehouseMap.messages.loadError', { message: errorMessage }), 'error');
      }

      // Ensure elements are cleared on error
      setElements([]);
      setHistory([]);
      setHistoryIndex(0);
    } finally {
      setLoading(false);
    }
  }, [showSnackbar, t]);

  // Load map data when warehouse changes
  useEffect(() => {
    if (selectedWarehouse) {
      loadMapData(selectedWarehouse.id);
    }
  }, [selectedWarehouse, loadMapData]);

  // Cleanup history debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (historyTimeoutRef.current) {
        clearTimeout(historyTimeoutRef.current);
      }
    };
  }, []);

  // Save changes
  const saveChanges = useCallback(async () => {
    if (!selectedWarehouse) return;

    setSaving(true);
    try {
      // Validate elements array
      if (!Array.isArray(elements) || elements.length === 0) {
        showSnackbar(t('warehouseMap.messages.noElementsToSave'), 'info');
        setSaving(false);
        return;
      }

      // Prepare locations data for bulk update, filtering out invalid elements
      const locationsData = elements
        .filter(el =>
          el &&
          el.id &&
          el.dimensions &&
          el.dimensions.width > 0 &&
          el.dimensions.height > 0 &&
          el.position &&
          isFinite(el.position.x) &&
          isFinite(el.position.y)
        )
        .map(el => {
          const isNew = el.id.startsWith('new-') || el.id.startsWith('scan-');
          return {
            id: isNew ? null : Number(el.id),
            temp_id: isNew ? el.id : undefined,  // For mapping response back
            name: el.name || t('warehouseMap.messages.noName'),
            type: el.type || 'RACK',
            position_x: el.position.x || 0,
            position_y: el.position.y || 0,
            width: Math.max(20, el.dimensions.width),
            height: Math.max(20, el.dimensions.height),
            rotation: el.rotation || 0,
            color: el.color || '#e3f2fd',
          };
        });

      // Detect deleted elements (were in DB but not in current elements)
      const currentElementIds = new Set(
        elements
          .filter(el => el && el.id && !el.id.startsWith('new-') && !el.id.startsWith('scan-'))
          .map(el => el.id)
      );

      const deletedIds: number[] = Array.from(initialElementIds)
        .filter(id => !currentElementIds.has(id))
        .map(id => Number(id))
        .filter(id => !isNaN(id) && isFinite(id));

      if (locationsData.length === 0 && deletedIds.length === 0) {
        showSnackbar(t('warehouseMap.messages.noChanges'), 'info');
        setSaving(false);
        return;
      }

      // Use axios instead of fetch to benefit from token refresh interceptor
      const response = await axios.post(
        `${API_BASE_URL}/warehouses/${selectedWarehouse.id}/bulk_update_positions/`,
        {
          locations: locationsData,
          deleted_ids: deletedIds.length > 0 ? deletedIds : undefined
        }
      );

      const result = response.data;

      // Update element IDs for newly created elements
      if (result.created_ids && Array.isArray(result.created_ids) && result.created_ids.length > 0) {
        setElements(prevElements => {
          if (!Array.isArray(prevElements)) return prevElements;
          return prevElements.map(el => {
            if (!el || !el.id) return el;
            const created = result.created_ids.find(
              (c: { temp_id: string; new_id: number }) => c && c.temp_id === el.id
            );
            if (created && created.new_id) {
              return { ...el, id: String(created.new_id) };
            }
            return el;
          });
        });
      }

      // Update initialElementIds after successful save
      // Add newly created IDs and remove deleted ones
      setElements(prevElements => {
        const newDbIds = new Set(initialElementIds);

        // Add newly created element IDs
        if (result.created_ids && Array.isArray(result.created_ids)) {
          result.created_ids.forEach((c: { temp_id: string; new_id: number }) => {
            if (c && c.new_id) {
              newDbIds.add(String(c.new_id));
            }
          });
        }

        // Remove deleted IDs
        deletedIds.forEach(id => {
          newDbIds.delete(String(id));
        });

        // Add all current element IDs that are not temp (new/scan)
        prevElements.forEach(el => {
          if (el && el.id && !el.id.startsWith('new-') && !el.id.startsWith('scan-')) {
            newDbIds.add(el.id);
          }
        });

        setInitialElementIds(newDbIds);
        return prevElements;
      });

      // Build success message
      const parts: string[] = [];
      if (result.updated_count > 0) {
        parts.push(t('warehouseMap.messages.updatedCount', { count: result.updated_count }));
      }
      if (result.created_count > 0) {
        parts.push(t('warehouseMap.messages.createdCount', { count: result.created_count }));
      }
      if (result.deleted_count > 0) {
        parts.push(t('warehouseMap.messages.deletedCount', { count: result.deleted_count }));
      }

      const msg = parts.length > 0
        ? t('warehouseMap.messages.saved', { details: parts.join(', ') })
        : t('warehouseMap.messages.changesSaved');
      showSnackbar(msg, 'success');
    } catch (error: any) {
      console.error('Failed to save:', error);

      // Handle axios errors
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.detail ||
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          t('warehouseMap.messages.unknownError');

        // Check if it's a token expiration error (should be handled by interceptor, but just in case)
        if (error.response?.status === 401) {
          showSnackbar(t('warehouseMap.messages.sessionExpired'), 'error');
        } else if (error.response?.status) {
          showSnackbar(t('warehouseMap.messages.serverError', { status: error.response.status, message: errorMessage }), 'error');
        } else if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
          showSnackbar(t('warehouseMap.messages.networkError'), 'error');
        } else {
          showSnackbar(t('warehouseMap.messages.saveError', { message: errorMessage }), 'error');
        }
      } else {
        const errorMessage = error instanceof Error ? error.message : t('warehouseMap.messages.unknownError');
        showSnackbar(t('warehouseMap.messages.saveError', { message: errorMessage }), 'error');
      }
    } finally {
      setSaving(false);
    }
  }, [selectedWarehouse, elements, initialElementIds, showSnackbar, t]);

  // Add element to history for undo/redo
  const addToHistory = useCallback((newElements: MapElement[]) => {
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(newElements);
      return newHistory;
    });
    setHistoryIndex(prev => prev + 1);
  }, [historyIndex]);

  // Place a single unplaced rack on the map
  const handlePlaceLocation = useCallback((loc: UnplacedLocation) => {
    const maxX = elements.reduce((m, el) => Math.max(m, el.position.x + el.dimensions.width), 0);
    const newElement: MapElement = {
      id: String(loc.id),
      code: loc.code,
      name: loc.name,
      type: loc.location_type as ElementType,
      position: { x: maxX + 20, y: 60 },
      dimensions: { width: 80, height: 40 },
      rotation: 0,
      color: '#e3f2fd',
      status: 'ACTIVE',
    };
    const newElements = [...elements, newElement];
    setElements(newElements);
    addToHistory(newElements);
    setUnplacedLocations(prev => prev.filter(l => l.id !== loc.id));
    setUnplacedCount(prev => prev - 1);
    showSnackbar(t('warehouseMap.unplaced.placed', { name: loc.name }), 'success');
  }, [elements, addToHistory, showSnackbar, t]);

  // Auto-place all unplaced locations
  const handleAutoPlaceAll = useCallback(async () => {
    if (!selectedWarehouse) return;
    try {
      await autoPlaceLocations(selectedWarehouse.id);
      await loadMapData(selectedWarehouse.id);
      await loadUnplacedLocations();
      showSnackbar(t('warehouseMap.unplaced.allPlaced'), 'success');
    } catch (err) {
      console.error('Failed to auto-place:', err);
      showSnackbar(t('warehouseMap.unplaced.error'), 'error');
    }
  }, [selectedWarehouse, loadMapData, loadUnplacedLocations, showSnackbar, t]);

  // Undo
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(prev => prev - 1);
      setElements(history[historyIndex - 1]);
    }
  }, [history, historyIndex]);

  // Redo
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(prev => prev + 1);
      setElements(history[historyIndex + 1]);
    }
  }, [history, historyIndex]);

  // Add new element
  const addElement = useCallback((type: ElementType, position?: Position) => {
    const typeConfig = ELEMENT_TYPES.find(t => t.type === type);
    if (!typeConfig) return;

    const elementTypeLabel = t(`warehouseMap.elementTypes.${type}`);
    const newElement: MapElement = {
      id: `new-${Date.now()}`,
      name: `${t('common.new')} ${elementTypeLabel}`,
      type,
      position: position || {
        x: 50 + Math.random() * 100,
        y: 50 + Math.random() * 100,
      },
      dimensions: {
        width: typeConfig.defaultWidth,
        height: typeConfig.defaultHeight,
      },
      rotation: 0,
      color: typeConfig.defaultColor,
      status: 'ACTIVE',
    };

    const newElements = [...elements, newElement];
    setElements(newElements);
    addToHistory(newElements);
    setSelectedElementId(newElement.id);
    showSnackbar(t('warehouseMap.messages.elementAdded', { type: elementTypeLabel }), 'success');
  }, [elements, addToHistory, showSnackbar, t]);

  // Update element
  const updateElement = useCallback((updates: Partial<MapElement>) => {
    if (!selectedElementId || !Array.isArray(elements)) return;

    const newElements = elements.map(el =>
      el && el.id === selectedElementId ? { ...el, ...updates } : el
    ).filter(el => el !== null && el !== undefined) as MapElement[];

    setElements(newElements);
    addToHistory(newElements);
  }, [elements, selectedElementId, addToHistory]);

  // Delete element
  const deleteElement = useCallback(() => {
    if (!selectedElementId || !Array.isArray(elements)) return;

    const elementToDelete = elements.find(el => el && el.id === selectedElementId);
    if (!elementToDelete) return;

    const newElements = elements.filter(el => el && el.id !== selectedElementId);
    setElements(newElements);
    addToHistory(newElements);
    setSelectedElementId(null);

    const elementName = elementToDelete.name || elementToDelete.code || t('warehouseMap.messages.element');
    showSnackbar(t('warehouseMap.messages.elementDeleted', { name: elementName }), 'info');
  }, [elements, selectedElementId, addToHistory, showSnackbar, t]);

  // Duplicate element
  const duplicateElement = useCallback(() => {
    if (!Array.isArray(elements)) return;

    const element = elements.find(el => el && el.id === selectedElementId);
    if (!element) return;

    const newElement: MapElement = {
      ...element,
      id: `new-${Date.now()}`,
      name: t('warehouseMap.messages.elementCopy', { name: element.name }),
      position: {
        x: element.position.x + 20,
        y: element.position.y + 20,
      },
    };

    const newElements = [...elements, newElement];
    setElements(newElements);
    addToHistory(newElements);
    setSelectedElementId(newElement.id);
    showSnackbar(t('warehouseMap.messages.elementDuplicated'), 'success');
  }, [elements, selectedElementId, addToHistory, showSnackbar, t]);

  // Debounced add to history - waits 300ms after last change to avoid spam during continuous transform
  const debouncedAddToHistory = useCallback((newElements: MapElement[]) => {
    pendingElementsRef.current = newElements;

    if (historyTimeoutRef.current) {
      clearTimeout(historyTimeoutRef.current);
    }

    historyTimeoutRef.current = setTimeout(() => {
      if (pendingElementsRef.current) {
        addToHistory(pendingElementsRef.current);
        pendingElementsRef.current = null;
      }
    }, 300);
  }, [addToHistory]);

  // Handle element move
  const handleElementMove = useCallback((id: string, position: Position) => {
    if (!id || !position || !isFinite(position.x) || !isFinite(position.y)) return;

    setElements(prevElements => {
      if (!Array.isArray(prevElements)) return prevElements;

      const newElements = prevElements
        .filter(el => el !== null && el !== undefined)
        .map(el =>
          el && el.id === id ? { ...el, position } : el
        )
        .filter(el => el !== null && el !== undefined) as MapElement[];

      debouncedAddToHistory(newElements);
      return newElements;
    });
  }, [debouncedAddToHistory]);

  // Handle element resize
  const handleElementResize = useCallback((id: string, width: number, height: number) => {
    if (!id || !isFinite(width) || !isFinite(height) || width <= 0 || height <= 0) return;

    setElements(prevElements => {
      if (!Array.isArray(prevElements)) return prevElements;

      const newElements = prevElements
        .filter(el => el !== null && el !== undefined)
        .map(el =>
          el && el.id === id ? { ...el, dimensions: { width, height } } : el
        )
        .filter(el => el !== null && el !== undefined) as MapElement[];

      debouncedAddToHistory(newElements);
      return newElements;
    });
  }, [debouncedAddToHistory]);

  // Handle element rotate
  const handleElementRotate = useCallback((id: string, rotation: number) => {
    if (!id || !isFinite(rotation)) return;

    setElements(prevElements => {
      if (!Array.isArray(prevElements)) return prevElements;

      const newElements = prevElements
        .filter(el => el !== null && el !== undefined)
        .map(el =>
          el && el.id === id ? { ...el, rotation } : el
        )
        .filter(el => el !== null && el !== undefined) as MapElement[];

      debouncedAddToHistory(newElements);
      return newElements;
    });
  }, [debouncedAddToHistory]);

  // Handle scan complete
  const handleScanComplete = useCallback((result: ScanResult, acceptedElements: DetectedElement[]) => {
    // Validate input
    if (!Array.isArray(acceptedElements)) {
      showSnackbar(t('warehouseMap.messages.invalidScanData'), 'error');
      return;
    }

    // Convert detected elements to map elements, filtering out invalid dimensions
    const newMapElements: MapElement[] = acceptedElements
      .filter(el =>
        el &&
        el.dimensions &&
        el.dimensions.width > 0 &&
        el.dimensions.height > 0 &&
        isFinite(el.dimensions.width) &&
        isFinite(el.dimensions.height) &&
        el.position &&
        isFinite(el.position.x) &&
        isFinite(el.position.y)
      )
      .map((el, index) => {
        const elementTypeLabel = t(`warehouseMap.elementTypes.${el.suggestedType}`);
        return {
          id: `scan-${Date.now()}-${index}`,
          name: `${elementTypeLabel}-${index + 1}`,
          type: el.suggestedType,
          position: el.position,
          dimensions: {
            width: Math.max(20, el.dimensions.width),  // Ensure minimum size
            height: Math.max(20, el.dimensions.height),
          },
          rotation: el.rotation || 0,
          color: el.suggestedColor,
          status: 'ACTIVE' as const,
          confidence: el.confidence,
        };
      });

    if (newMapElements.length === 0) {
      showSnackbar(t('warehouseMap.messages.noValidElements'), 'info');
      return;
    }

    const allElements = [...elements, ...newMapElements];
    setElements(allElements);
    addToHistory(allElements);
    setShowScanDialog(false);
    showSnackbar(t('warehouseMap.messages.elementsAddedFromScan', { count: newMapElements.length }), 'success');
  }, [elements, addToHistory, showSnackbar, t]);

  // Get selected element
  const selectedElement = elements.find(el => el.id === selectedElementId) || null;

  const selectedDbLocationId = useMemo(() => {
    if (!selectedElement) return null;
    const s = String(selectedElement.id);
    if (s.startsWith('new-') || s.startsWith('scan-')) return null;
    const n = Number(selectedElement.id);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [selectedElement]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'z':
            e.preventDefault();
            undo();
            break;
          case 'y':
            e.preventDefault();
            redo();
            break;
          case 's':
            e.preventDefault();
            saveChanges();
            break;
        }
      }
      if (e.key === 'Delete' && selectedElementId) {
        deleteElement();
      }
      if (e.key === 'Escape') {
        setSelectedElementId(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, saveChanges, deleteElement, selectedElementId]);

  const warehouseSelectMenuProps = {
    PaperProps: {
      sx: {
        backgroundColor: '#2a2a2a',
        color: '#ffffff',
        '& .MuiMenuItem-root': {
          color: '#ffffff',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
          '&.Mui-selected': {
            backgroundColor: 'rgba(143, 145, 143, 0.3)',
            '&:hover': { backgroundColor: 'rgba(143, 145, 143, 0.3)' },
          },
        },
      },
    },
  };

  const startPointValue = selectedWarehouse?.startPointX != null && selectedWarehouse?.startPointY != null
    ? { x: selectedWarehouse.startPointX, y: selectedWarehouse.startPointY, name: selectedWarehouse.startPointName || 'Biuro' }
    : null;

  /** Keeps toolbar buttons from wrapping/shrinking inside the horizontally scrollable row */
  const mapToolbarBtnSx = { flexShrink: 0, whiteSpace: 'nowrap' as const };

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header: row 1 = title + tabs + warehouse; row 2 = scrollable actions (desktop) */}
      <Paper
        elevation={0}
        sx={{
          p: isMobile ? 1 : 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? 1 : 1.5,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { md: 'center' },
            gap: 2,
            width: '100%',
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { md: 'center' },
              gap: 2,
              flex: 1,
              minWidth: 0,
            }}
          >
            {!isMobile && (
              <Typography
                variant="h5"
                fontWeight={600}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: { md: 260, lg: 360 },
                  flexShrink: 0,
                }}
              >
                {t('warehouseMap.title')}
              </Typography>
            )}

            {isMobile ? (
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={mainTab}
                onChange={(_, v) => {
                  if (v != null) {
                    setMainTab(v);
                    if (v === 'locations') setSelectedElementId(null);
                  }
                }}
                color="primary"
                sx={{
                  '& .MuiToggleButton-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    fontWeight: 600,
                  },
                  '& .MuiToggleButton-root.Mui-selected': {
                    color: '#ffffff',
                  },
                }}
              >
                <ToggleButton value="map">{t('warehouseMap.tabMap')}</ToggleButton>
                <ToggleButton value="locations">{t('warehouseMap.tabLocations')}</ToggleButton>
              </ToggleButtonGroup>
            ) : (
              <Tabs
                value={mainTab}
                onChange={(_, v) => {
                  if (v === 'map' || v === 'locations') {
                    setMainTab(v);
                    if (v === 'locations') setSelectedElementId(null);
                  }
                }}
                sx={{
                  minHeight: 40,
                  flexShrink: 0,
                  '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 },
                }}
              >
                <Tab value="map" label={t('warehouseMap.tabMap')} />
                <Tab value="locations" label={t('warehouseMap.tabLocations')} />
              </Tabs>
            )}
          </Box>

          <FormControl
            size="small"
            sx={{
              minWidth: { xs: '100%', md: 200 },
              maxWidth: { md: 320 },
              width: { xs: '100%', md: 'auto' },
              flexShrink: 0,
            }}
          >
            <InputLabel>{t('warehouseMap.warehouse')}</InputLabel>
            <Select
              value={selectedWarehouse?.id || ''}
              label={t('warehouseMap.warehouse')}
              onChange={(e) => {
                const wh = warehouses.find(w => w.id === Number(e.target.value));
                setSelectedWarehouse(wh || null);
              }}
              MenuProps={warehouseSelectMenuProps}
            >
              {Array.isArray(warehouses) && warehouses.map((wh) => (
                wh && (
                  <MenuItem key={wh.id} value={wh.id}>
                    {wh.name} - {wh.city}
                  </MenuItem>
                )
              ))}
            </Select>
          </FormControl>
        </Box>

        {isMobile ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
            <IconButton
              onClick={() => setViewMode(viewMode === '2d' ? '3d' : '2d')}
              disabled={!selectedWarehouse}
              sx={{ color: '#ffffff' }}
            >
              {viewMode === '3d' ? <View3DIcon /> : <View2DIcon />}
            </IconButton>
            <IconButton
              onClick={() => setShowFinder(!showFinder)}
              disabled={!selectedWarehouse}
              sx={{ color: '#ffffff' }}
            >
              <SearchIcon />
            </IconButton>
            <IconButton onClick={() => setMobileDrawerOpen(true)} sx={{ color: '#ffffff' }}>
              <MenuIcon />
            </IconButton>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
              pb: 0.5,
              width: '100%',
              minWidth: 0,
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: 3,
                bgcolor: 'action.disabledBackground',
              },
            }}
          >
            {unplacedCount > 0 && (
              <Chip
                icon={<WarningIcon />}
                label={t('warehouseMap.unplaced.badge', { count: unplacedCount })}
                color="warning"
                variant="outlined"
                onClick={() => setShowUnplacedPanel(!showUnplacedPanel)}
                sx={{ fontWeight: 600, flexShrink: 0 }}
              />
            )}

            <Tooltip title={t('warehouseMap.undo')}>
              <span style={{ display: 'inline-block', flexShrink: 0 }}>
                <IconButton onClick={undo} disabled={historyIndex <= 0}>
                  <UndoIcon sx={{ color: '#ffffff' }} />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={t('warehouseMap.redo')}>
              <span style={{ display: 'inline-block', flexShrink: 0 }}>
                <IconButton onClick={redo} disabled={historyIndex >= history.length - 1}>
                  <RedoIcon sx={{ color: '#ffffff' }} />
                </IconButton>
              </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ flexShrink: 0 }} />

            <Button
              size="small"
              variant="contained"
              startIcon={<ScanIcon />}
              onClick={() => setShowScanDialog(true)}
              disabled={!selectedWarehouse}
              sx={mapToolbarBtnSx}
            >
              {t('warehouseMap.scanPlan')}
            </Button>

            <Button
              size="small"
              variant="outlined"
              startIcon={<ImportIcon />}
              onClick={() => setShowImportDialog(true)}
              disabled={!selectedWarehouse}
              sx={mapToolbarBtnSx}
            >
              {t('warehouseMap.importJson')}
            </Button>

            <Button
              size="small"
              variant={mode === 'set_start_point' ? 'contained' : 'outlined'}
              startIcon={<HomeIcon />}
              onClick={() => setMode(mode === 'set_start_point' ? 'select' : 'set_start_point' as EditorMode)}
              disabled={!selectedWarehouse}
              color={mode === 'set_start_point' ? 'success' : 'inherit'}
              sx={{
                ...mapToolbarBtnSx,
                animation: mode === 'set_start_point' ? 'pulse-btn 1.5s infinite' : 'none',
                '@keyframes pulse-btn': {
                  '0%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.4)' },
                  '70%': { boxShadow: '0 0 0 8px rgba(76, 175, 80, 0)' },
                  '100%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)' },
                },
              }}
            >
              {t('warehouseMap.startPoint')}
            </Button>

            <Divider orientation="vertical" flexItem sx={{ flexShrink: 0 }} />

            {mainTab === 'map' && (
              <Button
                size="small"
                variant={viewMode === '3d' ? 'contained' : 'outlined'}
                startIcon={viewMode === '3d' ? <View3DIcon /> : <View2DIcon />}
                onClick={() => setViewMode(viewMode === '2d' ? '3d' : '2d')}
                disabled={!selectedWarehouse}
                color={viewMode === '3d' ? 'info' : 'inherit'}
                sx={mapToolbarBtnSx}
              >
                {viewMode === '3d' ? t('warehouseMap.view3d') : t('warehouseMap.view2d')}
              </Button>
            )}

            <Button
              size="small"
              variant={showFinder ? 'contained' : 'outlined'}
              startIcon={<SearchIcon />}
              onClick={() => setShowFinder(!showFinder)}
              disabled={!selectedWarehouse}
              color={showFinder ? 'warning' : 'inherit'}
              sx={mapToolbarBtnSx}
            >
              {t('warehouseMap.findProduct')}
            </Button>

            <Button
              size="small"
              variant="outlined"
              startIcon={printingBulkLabels ? <CircularProgress size={16} /> : <PrintIcon />}
              onClick={handlePrintAllShelfLabels}
              disabled={printingBulkLabels || printingLabelRowId != null || !selectedWarehouse}
              sx={mapToolbarBtnSx}
            >
              {t('warehouseMap.labelsButton')}
            </Button>

            <Button
              size="small"
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={saveChanges}
              disabled={saving || !selectedWarehouse}
              sx={mapToolbarBtnSx}
            >
              {t('warehouseMap.save')}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Mobile info banner */}
      {isMobile && (
        <Box sx={{ px: 2, py: 1, bgcolor: 'info.dark', display: 'flex', alignItems: 'center', gap: 1 }}>
          <DesktopIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption">
            {t('warehouseMap.mobile.editOnDesktop')}
          </Typography>
        </Box>
      )}

      {/* Unplaced locations panel (desktop only) */}
      {!isMobile && showUnplacedPanel && unplacedCount > 0 && (
        <Paper
          elevation={0}
          sx={{
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            bgcolor: 'rgba(255, 152, 0, 0.08)',
          }}
        >
          <WarningIcon color="warning" sx={{ fontSize: 18 }} />
          <Typography variant="body2" sx={{ mr: 1 }}>
            {t('warehouseMap.unplaced.title', { count: unplacedCount })}:
          </Typography>
          {unplacedLocations.slice(0, 8).map((loc) => (
            <Chip
              key={loc.id}
              label={loc.name || loc.code}
              size="small"
              icon={<AddLocationIcon />}
              onClick={() => handlePlaceLocation(loc)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
          {unplacedLocations.length > 8 && (
            <Typography variant="caption" color="text.secondary">
              +{unplacedLocations.length - 8} {t('warehouseMap.unplaced.more')}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" color="warning" onClick={handleAutoPlaceAll}>
            {t('warehouseMap.unplaced.placeAll')}
          </Button>
          <IconButton size="small" onClick={() => setShowUnplacedPanel(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      )}

      {/* Main content */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left panel - Element palette (desktop 2D map editor only) */}
        {!isMobile && viewMode === '2d' && mainTab === 'map' && (
          <Box
            sx={{
              width: 200,
              p: 2,
              borderRight: '1px solid',
              borderColor: 'divider',
              overflow: 'auto',
            }}
          >
            <ElementPalette
              onAddElement={addElement}
              disabled={!selectedWarehouse}
            />
          </Box>
        )}

        {/* Center - Map canvas (2D) or 3D view, or locations list */}
        <Box sx={{ flex: 1, p: viewMode === '3d' ? 0 : (isMobile ? 0.5 : 2), overflow: 'hidden' }}>
          {mainTab === 'locations' && selectedWarehouse ? (
            <WarehouseLocationsPanel
              warehouseId={selectedWarehouse.id}
              includeTypes={BULK_LABEL_INCLUDE_TYPES}
              onShowOnMap={(mapElementId, highlightIds) => {
                setMainTab('map');
                setViewMode('2d');
                setSelectedElementId(mapElementId);
                setHighlightedLocationIds((prev) => Array.from(new Set([...prev, ...highlightIds])));
              }}
              onPrintLabel={(locationId) => {
                openLocationLabelsPdf({ locationIds: [locationId] }, locationId);
              }}
              printingLabelId={printingLabelRowId}
            />
          ) : loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : selectedWarehouse ? (
            viewMode === '2d' ? (
              <MapCanvas
                width={selectedWarehouse.mapWidth || 1000}
                height={selectedWarehouse.mapHeight || 800}
                elements={elements}
                selectedElementId={selectedElementId}
                backgroundImage={selectedWarehouse.mapBackground}
                mode={isMobile ? 'select' : mode}
                showGrid={!isMobile && showGrid}
                gridSize={20}
                snapToGrid={!isMobile}
                onElementSelect={(el) => setSelectedElementId(el?.id || null)}
                onElementMove={handleElementMove}
                onElementResize={handleElementResize}
                onElementRotate={handleElementRotate}
                onModeChange={setMode}
                onToggleGrid={() => setShowGrid(!showGrid)}
                readOnly={isMobile}
                onRackClick={handleRackDoubleClick}
                startPoint={startPointValue}
                onStartPointSet={async (position) => {
                  if (!selectedWarehouse) return;
                  try {
                    await axios.post(
                      `${API_BASE_URL}/warehouses/${selectedWarehouse.id}/update_map_settings/`,
                      { start_point_x: position.x, start_point_y: position.y, start_point_name: 'Biuro' }
                    );
                    await refreshWarehouses();
                    showSnackbar('Punkt startowy został ustawiony', 'success');
                  } catch (err) {
                    console.error('Failed to set start point:', err);
                    showSnackbar('Nie udało się ustawić punktu startowego', 'error');
                  }
                }}
                navigationGraph={navigationGraph || undefined}
                showNavigation={showNavigation}
                onToggleNavigation={() => setShowNavigation(!showNavigation)}
                pickingPath={pickingPath || undefined}
                showPickingPath={showPickingPath}
                onTogglePickingPath={() => setShowPickingPath(!showPickingPath)}
                highlightedLocationIds={highlightedLocationIds}
              />
            ) : (
              <Warehouse3DView
                width={selectedWarehouse.mapWidth || 1000}
                height={selectedWarehouse.mapHeight || 800}
                elements={elements}
                navigationGraph={navigationGraph || undefined}
                pickingPath={pickingPath || undefined}
                showPickingPath={showPickingPath}
                showNavigation={showNavigation}
                startPoint={startPointValue}
                onRackClick={handleRackDoubleClick}
              />
            )
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography color="text.secondary">
                {t('warehouseMap.selectWarehouse')}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right panel - Properties or Product Finder (desktop only, hidden on Locations tab) */}
        {!isMobile && mainTab === 'map' && (
        <Box
          sx={{
            width: showFinder ? 340 : 280,
            borderLeft: '1px solid',
            borderColor: 'divider',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {showFinder ? (
            /* Product Finder Panel */
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  <SearchIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom' }} />
                  {t('warehouseMap.findProduct')}
                </Typography>
                <IconButton size="small" onClick={() => setShowFinder(false)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Product search */}
              <Autocomplete
                freeSolo
                options={productSearchResults}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : `${option.name} (${option.product_code || 'brak kodu'})`
                }
                loading={productSearchLoading}
                inputValue={productSearchQuery}
                onInputChange={(_, value) => handleProductSearch(value)}
                onChange={(_, value) => {
                  if (value && typeof value !== 'string') {
                    addProductToPick(value);
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.product_code || '—'} · Stan: {option.quantity} szt.
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Wyszukaj produkt..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <SearchIcon sx={{ color: 'text.disabled', mr: 0.5, fontSize: 18 }} />,
                    }}
                  />
                )}
              />

              {/* Selected products list */}
              {selectedProducts.length > 0 && (
                <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                  <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      Produkty do zebrania ({selectedProducts.length})
                    </Typography>
                  </Box>
                  <List dense disablePadding>
                    {selectedProducts.map(({ product, quantity }) => (
                      <ListItem
                        key={product.id}
                        sx={{ px: 1.5, py: 0.5 }}
                        secondaryAction={
                          <IconButton edge="end" size="small" onClick={() => removeProductFromPick(product.id)}>
                            <CloseIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                          </IconButton>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <InventoryIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                              {product.name}
                            </Typography>
                          }
                          secondary={product.product_code || '—'}
                        />
                        <TextField
                          type="number"
                          size="small"
                          value={quantity}
                          onChange={(e) => updateProductQuantity(product.id, parseInt(e.target.value) || 1)}
                          sx={{ width: 60, mr: 3 }}
                          inputProps={{ min: 1, style: { textAlign: 'center', padding: '4px 8px' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}

              {/* Strategy selector */}
              {selectedProducts.length > 0 && (
                <FormControl size="small" fullWidth>
                  <InputLabel>Strategia</InputLabel>
                  <Select
                    value={pickingStrategy}
                    label="Strategia"
                    onChange={(e) => setPickingStrategy(e.target.value as PickingStrategy)}
                  >
                    <MenuItem value="shortest">Najkrótsza ścieżka</MenuItem>
                    <MenuItem value="shortest_3d">Najkrótsza 3D</MenuItem>
                    <MenuItem value="fifo">FIFO</MenuItem>
                    <MenuItem value="expiry_first">Najkrótsze daty ważności</MenuItem>
                    <MenuItem value="rack_grouped">Grupuj po regałach</MenuItem>
                  </Select>
                </FormControl>
              )}

              {/* Calculate path button */}
              {selectedProducts.length > 0 && (
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={pickingPathLoading ? <CircularProgress size={16} color="inherit" /> : <RouteIcon />}
                  onClick={handleCalculatePath}
                  disabled={pickingPathLoading}
                  fullWidth
                  sx={{ fontWeight: 600 }}
                >
                  {pickingPathLoading ? 'Obliczanie...' : 'Pokaż ścieżkę'}
                </Button>
              )}

              {/* Picking path results */}
              {pickingPath && pickingPath.steps.length > 0 && (
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    flex: 1,
                    overflow: 'auto',
                    borderColor: 'warning.main',
                    borderWidth: 2,
                  }}
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      bgcolor: 'warning.main',
                      color: 'warning.contrastText',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography variant="caption" fontWeight={700}>
                      ŚCIEŻKA ({pickingPath.steps.length} kroków)
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => { setPickingPath(null); setShowPickingPath(false); }}
                      sx={{ color: 'inherit', p: 0.25 }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>

                  {/* Summary stats */}
                  {/* <Box sx={{ px: 1.5, py: 1, display: 'flex', gap: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Chip
                      icon={<DistanceIcon sx={{ fontSize: '14px !important' }} />}
                      label={formatDistance(pickingPath.totalDistance)}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: 11 }}
                    />
                    <Chip
                      icon={<TimeIcon sx={{ fontSize: '14px !important' }} />}
                      label={formatEstimatedTime(pickingPath.estimatedTimeSeconds)}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: 11 }}
                    />
                  </Box> */}

                  {/* Steps */}
                  <List dense disablePadding>
                    {pickingPath.steps.map((step, idx) => (
                      <ListItem
                        key={idx}
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 'none' },
                        }}
                      >
                        <Box
                          sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            bgcolor: 'warning.main',
                            color: 'warning.contrastText',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 11,
                            fontWeight: 700,
                            mr: 1,
                            flexShrink: 0,
                          }}
                        >
                          {step.order}
                        </Box>
                        <Box sx={{ overflow: 'hidden' }}>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {step.product.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {step.rack?.code ? `${step.rack.code} › ` : ''}{step.location.code} · {step.quantityToPick} szt.
                          </Typography>
                        </Box>
                      </ListItem>
                    ))}
                  </List>

                  {/* Unfulfilled items */}
                  {pickingPath.unfulfilledItems.length > 0 && (
                    <Box sx={{ px: 1.5, py: 1, bgcolor: 'error.dark', color: 'error.contrastText' }}>
                      <Typography variant="caption" fontWeight={600}>
                        Niedobór:
                      </Typography>
                      {pickingPath.unfulfilledItems.map((item, idx) => (
                        <Typography key={idx} variant="caption" display="block">
                          {item.productName}: brakuje {item.quantityMissing} szt.
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Paper>
              )}

              {/* Empty state */}
              {selectedProducts.length === 0 && !pickingPath && (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.disabled" textAlign="center" sx={{ px: 2 }}>
                    Wyszukaj produkty powyżej, dodaj je do listy i kliknij "Pokaż ścieżkę" aby wyznaczyć optymalną trasę po magazynie
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            /* Properties Panel */
            <Box sx={{ p: 2 }}>
              <PropertiesPanel
                element={selectedElement}
                onUpdate={updateElement}
                onDelete={deleteElement}
                onDuplicate={duplicateElement}
                onPrintLocationLabel={
                  selectedDbLocationId
                    ? () => openLocationLabelsPdf({ locationIds: [selectedDbLocationId] }, selectedDbLocationId)
                    : undefined
                }
                printingLocationLabel={selectedDbLocationId != null && printingLabelRowId === selectedDbLocationId}
              />
            </Box>
          )}
        </Box>
        )}
      </Box>

      {/* Mobile bottom drawer */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          PaperProps={{ sx: { maxHeight: '50vh', bgcolor: '#2a2a2a', borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}
        >
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" fontWeight={600}>{t('warehouseMap.title')}</Typography>
              <IconButton size="small" onClick={() => setMobileDrawerOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('warehouseMap.warehouse')}</InputLabel>
              <Select
                value={selectedWarehouse?.id || ''}
                label={t('warehouseMap.warehouse')}
                onChange={(e) => {
                  const wh = warehouses.find(w => w.id === Number(e.target.value));
                  setSelectedWarehouse(wh || null);
                  setMobileDrawerOpen(false);
                }}
                MenuProps={warehouseSelectMenuProps}
              >
                {Array.isArray(warehouses) && warehouses.map((wh) => (
                  wh && <MenuItem key={wh.id} value={wh.id}>{wh.name} - {wh.city}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant={viewMode === '3d' ? 'contained' : 'outlined'}
              startIcon={viewMode === '3d' ? <View3DIcon /> : <View2DIcon />}
              onClick={() => { setViewMode(viewMode === '2d' ? '3d' : '2d'); setMobileDrawerOpen(false); }}
              fullWidth
            >
              {viewMode === '3d' ? t('warehouseMap.view3d') : t('warehouseMap.view2d')}
            </Button>
          </Box>
        </Drawer>
      )}

      {/* Mobile product finder drawer */}
      {isMobile && showFinder && (
        <Drawer
          anchor="bottom"
          open={showFinder}
          onClose={() => setShowFinder(false)}
          PaperProps={{ sx: { maxHeight: '60vh', bgcolor: '#2a2a2a', borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                <SearchIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom' }} />
                {t('warehouseMap.findProduct')}
              </Typography>
              <IconButton size="small" onClick={() => setShowFinder(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Autocomplete
              freeSolo
              options={productSearchResults}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : `${option.name} (${option.product_code || 'brak kodu'})`
              }
              loading={productSearchLoading}
              inputValue={productSearchQuery}
              onInputChange={(_, value) => handleProductSearch(value)}
              onChange={(_, value) => {
                if (value && typeof value !== 'string') {
                  addProductToPick(value);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Wyszukaj produkt..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <SearchIcon sx={{ color: 'text.disabled', mr: 0.5, fontSize: 18 }} />,
                  }}
                />
              )}
            />
          </Box>
        </Drawer>
      )}

      {/* Rack detail dialog */}
      <RackDetailView
        open={rackDetailOpen}
        onClose={() => { setRackDetailOpen(false); setRackDetailLocationId(null); }}
        locationId={rackDetailLocationId}
        editable={!isMobile}
        onShelfChanged={() => {
          if (selectedWarehouse) loadMapData(selectedWarehouse.id);
        }}
      />

      {/* Scan dialog */}
      {showScanDialog && selectedWarehouse && (
        <ScanUploader
          warehouseId={selectedWarehouse.id}
          onScanComplete={handleScanComplete}
          onClose={() => setShowScanDialog(false)}
        />
      )}

      {/* Import JSON dialog */}
      {selectedWarehouse && (
        <MapImporter
          open={showImportDialog}
          onClose={() => setShowImportDialog(false)}
          warehouseId={selectedWarehouse.id}
          warehouseName={`${selectedWarehouse.name} - ${selectedWarehouse.city}`}
          onImportSuccess={async () => {
            // Refresh warehouses to pick up updated map dimensions
            await refreshWarehouses();
            loadMapData(selectedWarehouse.id);
            showSnackbar('Mapa została zaimportowana', 'success');
          }}
        />
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WarehouseMap;

